import { ALL_PERMISSIONS, type Permission } from "./permissions";

export interface Role {
  id: string;
  name: string;
  description: string;
  builtIn: boolean;
  permissions: Permission[];
}

const p = (...perms: Permission[]) => perms;

/**
 * Built-in roles, confirmed from the reference instance:
 * System Administrator, Platform Administrator, AppSec Admin, AppSec User, Report Viewer.
 * Built-ins are clonable into custom roles but not editable in place.
 */
export const BUILT_IN_ROLES: Role[] = [
  {
    id: "system-administrator",
    name: "System Administrator",
    description: "Full, unrestricted access to every organisation, setting, and user.",
    builtIn: true,
    permissions: [...ALL_PERMISSIONS],
  },
  {
    id: "platform-administrator",
    name: "Platform Administrator",
    description: "Manages projects, users, roles, and platform configuration.",
    builtIn: true,
    permissions: p(
      "targets:read", "targets:create", "targets:update", "targets:delete", "targets:scan",
      "scans:read", "scans:run", "scans:stop", "scans:compare", "scans:delete",
      "issues:read", "issues:transition", "api-keys:read", "api-keys:manage", "schedules:read", "schedules:manage", "alerts:read", "alerts:manage", "vulnerabilities:read", "vulnerabilities:mark", "vulnerabilities:retest",
      "ai-testing:toggle",
      "reports:read", "reports:generate", "reports:delete",
      "team:read", "team:manage", "roles:read", "roles:manage",
      "projects:read", "projects:manage",
      "scan-profiles:manage", "settings:manage",
    ),
  },
  {
    id: "appsec-admin",
    name: "AppSec Admin",
    description: "Runs and manages scanning across assigned projects.",
    builtIn: true,
    permissions: p(
      "targets:read", "targets:create", "targets:update", "targets:delete", "targets:scan",
      "scans:read", "scans:run", "scans:stop", "scans:compare", "scans:delete",
      "issues:read", "issues:transition", "api-keys:read", "api-keys:manage", "schedules:read", "schedules:manage", "alerts:read", "alerts:manage", "vulnerabilities:read", "vulnerabilities:mark", "vulnerabilities:retest",
      "ai-testing:toggle",
      "reports:read", "reports:generate", "reports:delete",
      "scan-profiles:manage",
    ),
  },
  {
    id: "appsec-user",
    name: "AppSec User",
    description: "Runs scans and triages vulnerabilities for assigned targets.",
    builtIn: true,
    permissions: p(
      "targets:read", "targets:scan",
      "scans:read", "scans:run", "scans:stop",
      "issues:read", "issues:transition", "api-keys:read", "api-keys:manage", "schedules:read", "schedules:manage", "alerts:read", "alerts:manage", "vulnerabilities:read", "vulnerabilities:mark", "vulnerabilities:retest",
      "ai-testing:toggle",
      "reports:read", "reports:generate",
    ),
  },
  {
    id: "report-viewer",
    name: "Report Viewer",
    description: "Read-only access to vulnerabilities and reports.",
    builtIn: true,
    permissions: p(
      "targets:read", "scans:read", "schedules:read", "issues:read", "alerts:read", "vulnerabilities:read", "reports:read",
    ),
  },
];

export function getRole(id: string, customRoles: Role[] = []): Role | undefined {
  return [...BUILT_IN_ROLES, ...customRoles].find((r) => r.id === id);
}
